import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, ArrowRight } from 'lucide-react';
import { trackRecord } from '../data/mock';
import { Button } from './ui/button';

const PortfolioSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Map property names to their case study slugs
  const caseStudySlugs = {
    'Jarmese Apartments (Anais Place)': '/case-studies/jarmese-apartments',
    'Hollyvale Apartments': '/case-studies/hollyvale-apartments',
    'Northgreen Atrium Building': '/case-studies/northgreen-atrium',
    'Griggs Shopping Center': '/case-studies/griggs-shopping-center'
  };

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % trackRecord.length);
  }, []);

  const prevSlide = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + trackRecord.length) % trackRecord.length);
  };

  // Auto-scroll effect - 5 seconds interval
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  const currentProperty = trackRecord[currentIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    })
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section id="portfolio" className="relative gradient-section-alt py-12 sm:py-16 lg:py-24 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-48 sm:w-80 h-48 sm:h-80 bg-[#1a2940]/50 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 lg:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full mb-4 sm:mb-5 shadow-lg shadow-[#D4AF37]/20"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-label text-[#0A1628]">Track Record</span>
          </motion.div>
          <motion.h2 
            className="heading-h2 mb-3 sm:mb-4"
            variants={itemVariants}
          >
            Proven Results <span className="gradient-text">Since 2005</span>
          </motion.h2>
          <motion.p 
            className="text-body text-sm sm:text-base"
            variants={itemVariants}
          >
            Our portfolio demonstrates a decade of successful multifamily, retail, and office acquisitions 
            across the Greater Houston market.
          </motion.p>
        </motion.div>

        {/* Property Carousel Card */}
        <motion.div 
          className="glass-strong border border-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-10 mb-6 sm:mb-8 overflow-hidden"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Progress Bar */}
          <div className="w-full h-1 bg-white/10 rounded-full mb-4 sm:mb-6 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: isPaused ? `${((currentIndex + 1) / trackRecord.length) * 100}%` : '100%' }}
              transition={{ 
                duration: isPaused ? 0 : 5, 
                ease: 'linear',
                repeat: isPaused ? 0 : 0
              }}
              key={currentIndex}
            />
          </div>
          
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div 
              key={currentIndex}
              className="flex flex-col lg:grid lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: "easeInOut" }}
            >
              {/* Property Image - Shows first on mobile */}
              <motion.div 
                className="relative order-1 lg:order-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <div className="aspect-[16/10] sm:aspect-[4/3] card-modern rounded-xl sm:rounded-2xl overflow-hidden border border-white/10">
                  <img 
                    src={currentProperty.image} 
                    alt={currentProperty.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>

              {/* Content - Shows second on mobile */}
              <div className="order-2 lg:order-1 flex flex-col">
                {/* Status Badge */}
                <motion.div 
                  className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full mb-3 sm:mb-4 self-start ${
                    currentProperty.status === 'Realized' 
                      ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                      : 'bg-green-500/20 text-green-400'
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <span className="text-xs sm:text-sm font-semibold">{currentProperty.status}</span>
                </motion.div>

                {/* Property Name */}
                <motion.h3 
                  className="text-white text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  {currentProperty.name}
                </motion.h3>

                {/* Address */}
                <motion.div 
                  className="flex items-center gap-2 text-white/50 mb-4 sm:mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{currentProperty.location}</span>
                </motion.div>

                {/* Property Details - Mobile Optimized Grid */}
                <motion.div 
                  className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.25 }}
                >
                  {[
                    { label: 'Type', value: currentProperty.type },
                    { label: 'Size', value: currentProperty.units },
                    { label: 'Acquired', value: currentProperty.acquired }
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="card-modern rounded-lg sm:rounded-xl p-2.5 sm:p-3 lg:p-4 text-center sm:text-left"
                    >
                      <span className="text-white/40 text-[10px] sm:text-xs block mb-0.5 sm:mb-1">{item.label}</span>
                      <span className="text-white font-semibold text-xs sm:text-sm lg:text-base leading-tight block sm:truncate lg:whitespace-normal">{item.value}</span>
                    </div>
                  ))}
                </motion.div>

                {/* IRR if Realized */}
                {currentProperty.status === 'Realized' && currentProperty.irr !== '-' && (
                  <motion.div 
                    className="py-3 sm:py-4 border-t border-white/10 mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-4 sm:gap-6">
                      <div>
                        <span className="text-white/40 text-[10px] sm:text-xs block mb-0.5">IRR</span>
                        <span className="gradient-text text-lg sm:text-xl lg:text-2xl font-bold">{currentProperty.irr}</span>
                      </div>
                      {currentProperty.multiple !== '-' && (
                        <div>
                          <span className="text-white/40 text-[10px] sm:text-xs block mb-0.5">Multiple</span>
                          <span className="text-white text-lg sm:text-xl lg:text-2xl font-bold">{currentProperty.multiple}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* View Case Study Button */}
                {caseStudySlugs[currentProperty.name] && (
                  <motion.div 
                    className="mt-auto pt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                  >
                    <Link to={caseStudySlugs[currentProperty.name]}>
                      <Button 
                        className="group bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-4 sm:px-5 py-2 sm:py-2.5 h-9 sm:h-10 rounded-full text-xs sm:text-sm transition-all duration-300 inline-flex items-center gap-2 shadow-md shadow-[#D4AF37]/20 hover:shadow-lg hover:shadow-[#D4AF37]/30 w-full sm:w-auto justify-center"
                        data-testid={`view-case-study-${currentIndex}`}
                      >
                        View Case Study
                        <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Navigation */}
        <motion.div 
          className="flex items-center justify-center gap-3 sm:gap-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <motion.button 
            onClick={prevSlide}
            className="w-10 h-10 sm:w-12 sm:h-12 glass border border-white/10 rounded-full flex items-center justify-center hover:border-[#D4AF37]/50 transition-all duration-300 touch-manipulation"
            whileHover={{ scale: 1.1, borderColor: 'rgba(212, 175, 55, 0.5)' }}
            whileTap={{ scale: 0.95 }}
            aria-label="Previous property"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white/60" />
          </motion.button>
          
          {/* Dots */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {trackRecord.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 touch-manipulation ${
                  index === currentIndex 
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8962E] w-6 sm:w-8' 
                    : 'bg-white/20 w-2 sm:w-2.5 hover:bg-white/40'
                }`}
                whileTap={{ scale: 0.9 }}
                aria-label={`Go to property ${index + 1}`}
              />
            ))}
          </div>

          <motion.button 
            onClick={nextSlide}
            className="w-10 h-10 sm:w-12 sm:h-12 glass border border-white/10 rounded-full flex items-center justify-center hover:border-[#D4AF37]/50 transition-all duration-300 touch-manipulation"
            whileHover={{ scale: 1.1, borderColor: 'rgba(212, 175, 55, 0.5)' }}
            whileTap={{ scale: 0.95 }}
            aria-label="Next property"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white/60" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PortfolioSection;
